import { ChevronRight, Edit, TrashIcon } from 'lucide-react';
import { useContext } from 'react';
import { useTranslate } from 'sbx-crm-menu-ui';
import Permission from 'src/components/shared/Permission';
import { AI_AGENT_V2_DELETE, AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { AppAgentContext } from '../../../../../context/ContextProvider';
import { appActions } from '../../../../../store';
import { AgentSkills } from '../../../../../types/AiAgentV2';
import { ModalType } from '../../../../../types/Modal';
import { temporallyLabelOpenAiToOracle } from '../../../../../utils';
import { Button } from '../../../../ui/button';
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../../ui/card';
import { AgentSetupContext } from '../../AgentSetupComponent';
import AgentSkillDetailModalComponent from './AgentSkillDetailModalComponent';

type Props = {
    skill: AgentSkills;
    isNew?: boolean;
    removeSkill?: () => void;
    updateSkill?: () => void;
};
const AgentSkillDetailComponent = ({ skill, isNew, removeSkill, updateSkill }: Props) => {
    const { t: agent } = useTranslate('agent');
    const { t: common } = useTranslate('common');
    const { skillFiles, setSkillFiles } = useContext(AgentSetupContext);
    const { dispatch: dispatchApp } = useContext(AppAgentContext);

    const openSkillDetail = (skill: AgentSkills) => {
        dispatchApp(
            appActions.openModal({
                type: ModalType.DYNAMIC,
                data: {
                    title: `${agent('skill')}: ${skill.name || ''}`,
                    titleClassName: 'text-2xl font-bold text-purple-800',
                    className:
                        'bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl shadow-2xl w-full max-w-4xl overflow-hidden  ',
                    children: (
                        <AgentSkillDetailModalComponent
                            setSkillFiles={setSkillFiles}
                            skillFiles={skillFiles}
                            skill={skill}
                        />
                    ),

                    onClose() {
                        dispatchApp(appActions.closeModal(ModalType.DYNAMIC));
                    },
                },
            })
        );
    };

    return (
        <Card
            key={skill.id}
            className="flex flex-col bg-gradient-to-br from-blue-100 to-purple-100 hover:shadow-xl transition-all duration-300 transform hover:scale-105"
        >
            <CardHeader>
                <CardTitle className="flex items-center justify-between">
                    <span className="flex justify-between items-center text-xl font-bold text-indigo-800">
                        {skill.name}
                    </span>
                    <div className="flex gap-2 items-center">
                        <Permission permission={AI_AGENT_V2_EDIT}>
                            <button onClick={updateSkill}>
                                <Edit className="icon-size text-purple-600 hover:text-purple-900 mr-2" />
                            </button>
                        </Permission>
                        <Permission permission={AI_AGENT_V2_DELETE}>
                            <button onClick={removeSkill}>
                                <TrashIcon className="icon-size text-red-500" />
                            </button>
                        </Permission>
                    </div>
                </CardTitle>
                <CardDescription className="text-indigo-600">
                    {skill.description || agent('no_description_provided')}
                </CardDescription>
            </CardHeader>

            <div className="flex flex-col px-6 mb-4">
                <div>
                    <span className="text-sm text-indigo-700 font-bold">{agent('skill_type')}: </span>
                    <span className="text-sm font-medium">{agent(skill.skill_type) ?? skill.skill_type}</span>
                </div>
                <div>
                    <span className="text-sm text-indigo-700 font-bold">{common('model')}: </span>
                    <span className="text-sm">{temporallyLabelOpenAiToOracle(skill.model)}</span>
                </div>
            </div>

            {!isNew && (
                <CardFooter>
                    <Button
                        variant="outline"
                        className="w-full !bg-white !bg-opacity-50 !hover:bg-opacity-75 !text-indigo-700 !border-indigo-300 hover:border-indigo-500 transition-all duration-300"
                        onClick={() => openSkillDetail(skill)}
                    >
                        {agent('see_details')}
                        <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                </CardFooter>
            )}
        </Card>
    );
};

export default AgentSkillDetailComponent;
